import { motion } from "framer-motion";
import { Truck, ShieldCheck, Headphones, Gift, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useStore, api } from "@/context/StoreContext";

const Skyline = () => (
  <svg viewBox="0 0 560 130" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <g fill="#7bc9ef">
      {[0, 96, 192, 288, 384, 480].map((x, i) => (
        <g key={i} opacity={i % 2 ? 0.65 : 1}>
          <path
            d={`M${x + 10} 130 L${x + 10} 80 C${x + 10} 52 ${x + 20} 36 ${x + 30} 14 C${x + 40} 36 ${x + 50} 52 ${x + 50} 80 L${x + 50} 130 Z`}
          />
          <rect x={x} y="100" width="60" height="12" rx="5" />
        </g>
      ))}
      <rect x="0" y="118" width="560" height="12" rx="6" />
    </g>
  </svg>
);

const BENEFITS = [
  { icon: Truck, title: "benefit.delivery", sub: "benefit.deliverySub", testid: "benefit-delivery" },
  { icon: ShieldCheck, title: "benefit.payment", sub: "benefit.paymentSub", testid: "benefit-payment" },
  { icon: Headphones, title: "benefit.support", sub: "benefit.supportSub", testid: "benefit-support" },
  { icon: Gift, title: "benefit.offers", sub: "benefit.offersSub", testid: "benefit-offers" },
];

const rise = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export default function Support() {
  const { t, API, toast, user } = useStore();
  const [circleOpen, setCircleOpen] = useState(false);
  const [circleDone, setCircleDone] = useState(false);
  const [circleStatus, setCircleStatus] = useState("");
  const [circleError, setCircleError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [agree, setAgree] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // Pre-fill from the signed-in account; never create a duplicate membership.
  useEffect(() => {
    if (user && circleOpen) {
      setName((n) => n || user.name || "");
      setEmail((v) => v || user.email || "");
    }
  }, [user, circleOpen]);

  const join = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setCircleError(t("toast.memberEmpty"));
      return;
    }
    if (!agree) {
      setCircleError(t("member.agreeRequired"));
      return;
    }
    setCircleError("");
    setBusy(true);
    try {
      const { data } = await api.post("/members/join", {
        name: name.trim(), email: email.trim(), agreement: agree, marketing,
      });
      if (data.already_member) {
        setCircleError("");
        toast(t("member.alreadyCircle"));
        setCircleDone(true);
        setCircleStatus("already_member");
      } else if (data.joined) {
        setCircleStatus(data.membership_status || "pending_verification");
        setCircleDone(true);
        if (data.membership_status === "active") {
          toast(t("member.welcomeActive"));
        } else {
          toast(t("member.successToast"));
        }
      }
    } catch (err) {
      setCircleError(err.response?.data?.detail || t("toast.tryAgain"));
    }
    setBusy(false);
  };

  const verifyGuest = async (e) => {
    e.preventDefault();
    setCircleError("");
    setBusy(true);
    try {
      const { data } = await api.post("/members/verify", { email: email.trim(), code: code.trim() });
      if (data.verified) {
        setCircleStatus("active");
        toast(t("member.welcomeActive"));
        setCircleDone(true);
      }
    } catch (err) {
      setCircleError(err.response?.data?.detail || t("toast.tryAgain"));
    }
    setBusy(false);
  };

  return (
    <section className="section" id="support" data-testid="support-section">
      <div className="container">
        <motion.div className="benefits" {...rise}>
          {BENEFITS.map((b) => (
            <div className="benefit" key={b.testid} data-testid={b.testid}>
              <div className="benefit-icon"><b.icon size={22} /></div>
              <div>
                <h4>{t(b.title)}</h4>
                <p>{t(b.sub)}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <motion.div className="cambodia" {...rise}>
          <div>
            <p className="section-title-small">{t("home.eyebrow")}</p>
            <h2>
              {t("home.title1")}
              <br />
              {t("home.title2")}
            </h2>
            <p>{t("home.desc")}</p>
          </div>
          <div className="skyline"><Skyline /></div>
        </motion.div>

        <motion.div className="membership" {...rise}>
          <div className="membership-copy">
            <p className="section-title-small">{t("member.eyebrow")}</p>
            <h2>{t("member.title")}</h2>
            <p className="muted">{t("member.pitch")}</p>
          </div>
          <button className="checkout-btn member-join-btn" data-testid="membership-join-button" onClick={() => setCircleOpen(true)}>
            {t("member.joinBtn")}
          </button>
        </motion.div>
      </div>

      {circleOpen && (
        <div className="modal show" data-testid="circle-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setCircleOpen(false); }}>
          <div className="modal-card circle-card" data-testid="circle-modal" data-lenis-prevent>
            <div className="modal-head">
              <div>
                <p className="section-title-small">{t("member.eyebrow")}</p>
                <h2>{t("member.modalTitle")}</h2>
              </div>
              <button className="close-btn" data-testid="circle-close-button" aria-label="Close" onClick={() => setCircleOpen(false)}>
                <X size={18} />
              </button>
            </div>

            {circleDone ? (
              <div className="circle-done" data-testid="circle-done-view">
                <p className="success-note" data-testid="circle-success">{t("member.successTitle")}<br />{t("member.successDesc")}</p>
                {circleStatus === "pending_verification" && (
                  <form className="auth-form" onSubmit={verifyGuest} data-testid="circle-verify-view">
                    <p className="muted">{t("member.verifyHere")}</p>
                    <div className="field">
                      <label>{t("auth.codeLabel")}</label>
                      <input
                        data-testid="circle-verify-code-input"
                        className="code-input"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="000000"
                        required
                      />
                    </div>
                    <p className="error-text" data-testid="circle-verify-error">{circleError}</p>
                    <button className="checkout-btn" type="submit" data-testid="circle-verify-button" disabled={busy}>
                      {t("member.verifyBtn")}
                    </button>
                  </form>
                )}
                <button className="checkout-btn" data-testid="circle-done-close" onClick={() => setCircleOpen(false)}>
                  {t("auth.done")}
                </button>
              </div>
            ) : (
              <div data-testid="circle-form-view">
                <p className="muted circle-welcome">{t("member.modalWelcome")}</p>
                <form className="circle-form" onSubmit={join} data-testid="circle-form">
                  <input
                    data-testid="circle-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("member.namePlaceholder")}
                    aria-label={t("member.namePlaceholder")}
                    required
                  />
                  <input
                    data-testid="circle-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("member.emailPlaceholder")}
                    aria-label={t("member.emailPlaceholder")}
                    required
                  />
                  <label className="circle-consent" data-testid="circle-agreement-row">
                    <input
                      type="checkbox"
                      data-testid="circle-agreement-checkbox"
                      checked={agree}
                      onChange={(e) => setAgree(e.target.checked)}
                    />
                    <span>{t("member.agreeComms")}</span>
                  </label>
                  <label className="circle-consent" data-testid="circle-marketing-row">
                    <input
                      type="checkbox"
                      data-testid="circle-marketing-checkbox"
                      checked={marketing}
                      onChange={(e) => setMarketing(e.target.checked)}
                    />
                    <span>{t("member.marketing")}</span>
                  </label>
                  <p className="error-text" data-testid="circle-error">{circleError}</p>
                  <button className="checkout-btn pay-btn" type="submit" data-testid="circle-join-button" disabled={busy}>
                    {t("member.modalJoinBtn")}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
